import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import api from "../utils/api";
import HeathrowFlightSelector from "../components/HeathrowFlightSelector";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/ArrivalAssistant.css";
import { flightNumKey } from "../utils/heathrowFlightSelection";

const todayUpdatedLabel = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "Europe/London",
}).format(new Date());

const ArrivalAssistantVisitor = () => {
  const location = useLocation();
  const STORAGE_KEY = "ccg_arrival_visitor_token";
  const DEVICE_KEY = "ccg_arrival_device_id";

  const getTokenFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("s");
  };

  const [flightNumber, setFlightNumber] = useState("KU103");
  const [flightDirection, setFlightDirection] = useState("Arrival");
  const [flightDate, setFlightDate] = useState("");
  const [flightAnchorUtc, setFlightAnchorUtc] = useState("");
  const [nationalityGroup, setNationalityGroup] = useState("NonEEA");
  const [visitPurpose, setVisitPurpose] = useState("tourist");
  const [ageGroup, setAgeGroup] = useState("adult");
  const [firstTimeUk, setFirstTimeUk] = useState(true);
  const [visaType, setVisaType] = useState("none");
  const [travellingWithChildren, setTravellingWithChildren] = useState(false);
  const [travellingWithElderly, setTravellingWithElderly] = useState(false);
  const [onwardMode, setOnwardMode] = useState("undecided");
  const [passengers, setPassengers] = useState(1);
  const [bags, setBags] = useState(2);
  const [hoveredSection, setHoveredSection] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [checklistData, setChecklistData] = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistError, setChecklistError] = useState(null);
  const [email, setEmail] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [copyStatus, setCopyStatus] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [emailHint, setEmailHint] = useState(null);
  const [sessionMeta, setSessionMeta] = useState(null);
  const [otpStep, setOtpStep] = useState("idle");
  const [otpCode, setOtpCode] = useState("");
  const [statusJson, setStatusJson] = useState(null);
  const [resumeEmail, setResumeEmail] = useState("");
  const [resumeStatus, setResumeStatus] = useState(null);
  const [activeSection, setActiveSection] = useState("pre_arrival");

  const savedLink = sessionToken
    ? `${window.location.origin}/arrival-assistant-visitor?s=${sessionToken}`
    : "";

  const handleCopyLink = async () => {
    if (!savedLink) return;
    try {
      await navigator.clipboard.writeText(savedLink);
      setCopyStatus("copied");
    } catch (err) {
      console.error("Copy failed", err);
      setCopyStatus("error");
    }
    setTimeout(() => setCopyStatus(null), 2000);
  };

  const fetchSessionMeta = async (tokenValue, opts = {}) => {
    if (!tokenValue) return;
    setChecklistLoading(true);
    setChecklistError(null);
    try {
      let deviceId = null;
      try {
        deviceId = localStorage.getItem(DEVICE_KEY);
      } catch (eLS) {
        deviceId = null;
      }
      const resp = await api.get(`/api/open/arrival-assistant/session/${tokenValue}`, {
        headers: deviceId ? { "x-aa-device": deviceId } : undefined
      });

      if (resp.data?.verification_required && !opts.skipVerificationCheck) {
        setVerificationRequired(true);
        const hint = resp.data.email_hint || null;
        setEmailHint(hint);
        setEmail("");
        setChecklistData(null);
        setStatusJson(null);
        setResult(null);
        return resp.data;
      }
      // If caller asked to skip the verification gate (after OTP success), proceed anyway.
      if (resp.data?.verification_required && opts.skipVerificationCheck) {
        setTimeout(() => fetchSessionMeta(tokenValue), 250);
        return resp.data;
      }

      setVerificationRequired(false);
      setEmailHint(null);
      setSessionMeta({
        email: resp.data.email || null,
        email_verified: resp.data.email_verified || false,
        flow_version: resp.data.flow_version
      });

      if (resp.data.sections) {
        setChecklistData(resp.data);
      }
      if (resp.data.status_json) {
        setStatusJson(resp.data.status_json);
      }

      const savedInputs = resp.data.inputs_json;
      if (savedInputs?.flight_number) {
        setLoading(true);
        setError(null);
        try {
          const guideRes = await api.post("/api/arrival-assistant", {
            ...savedInputs,
            visit_purpose: "tourist",
            visa_type: savedInputs.visa_type || "none"
          });
          setResult(guideRes.data);
        } catch (err) {
          console.error("❌ Guide regen error:", err);
        } finally {
          setLoading(false);
        }
        setFlightNumber(savedInputs.flight_number || "KU103");
        setFlightDirection(savedInputs.direction || "Arrival");
        setFlightDate(savedInputs.flight_date || "");
        setFlightAnchorUtc(savedInputs.anchor_utc || "");
        setNationalityGroup(savedInputs.nationality_group || "NonEEA");
        setVisitPurpose("tourist");
        setAgeGroup(savedInputs.age_group || "adult");
        setFirstTimeUk(savedInputs.first_time_uk ?? true);
        setVisaType(savedInputs.visa_type || "none");
        setTravellingWithChildren(savedInputs.travelling_with_children ?? false);
        setTravellingWithElderly(savedInputs.travelling_with_elderly ?? false);
        setPassengers(savedInputs.passengers || 1);
        setBags(savedInputs.bags || 2);
        setOnwardMode(savedInputs.onward_travel_mode || "undecided");
      }

      return resp.data;
    } catch (err) {
      console.error("❌ Session restore error:", err);
      setChecklistError("Could not restore your saved checklist.");
      return null;
    } finally {
      setChecklistLoading(false);
      setLoading(false);
    }
  };

  const updateChecklistItem = async (sectionKey, itemKey, checked) => {
    if (!sessionToken) return;

    setChecklistData(prev => {
      if (!prev?.sections?.[sectionKey]) return prev;
      const section = prev.sections[sectionKey];
      const items = section.items.map(i => (i.key === itemKey ? { ...i, checked } : i));
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionKey]: { ...section, items }
        }
      };
    });

    try {
      const res = await api.post(
        `/api/open/arrival-assistant/session/${sessionToken}/checklist`,
        { section_key: sectionKey, item_key: itemKey, checked }
      );

      setChecklistData(prev => ({
        ...prev,
        sections: {
          ...(prev?.sections || {}),
          [sectionKey]: {
            ...(prev?.sections?.[sectionKey] || {}),
            ...res.data
          }
        }
      }));
    } catch (err) {
      console.error("❌ Checklist update error:", err);
      setChecklistError("Could not save your checklist tick. Please try again.");
    }
  };

  const sendChecklistEmail = async () => {
    if (!sessionToken || !email) return;
    setEmailSending(true);
    setEmailSent(false);
    setEmailError(null);
    try {
      await api.post(`/api/open/arrival-assistant/session/${sessionToken}/email`, { email: email.trim() });
      setEmailSent(true);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        "We could not send the email. Please try again.";
      setEmailError(msg);
    } finally {
      setEmailSending(false);
    }
  };

  const sendOtp = async () => {
    if (!sessionToken || !email) {
      setEmailError("Enter your email to send a code.");
      return;
    }
    try {
      setEmailError(null);
      const resp = await api.post("/api/open/arrival/session/email/start", {
        token: sessionToken,
        email: email.trim()
      });
      setOtpStep("sent");
      if (resp.data?.email) setEmailHint(resp.data.email);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        "We could not send the code. Please try again.";
      setEmailError(msg);
    }
  };

  const verifyOtp = async () => {
    if (!sessionToken || !otpCode) {
      setEmailError("Enter the code you received.");
      return;
    }
    let ok = false;
    try {
      setOtpStep("verifying");
      const resp = await api.post("/api/open/arrival/session/email/verify", {
        token: sessionToken,
        code: otpCode.trim()
      });
      if (resp.data?.success) {
        if (resp.data?.device_id) {
          try {
            localStorage.setItem(DEVICE_KEY, resp.data.device_id);
          } catch (eLS) {
            console.warn("device id save skipped", eLS);
          }
        }
        ok = true;
        setOtpStep("verified");
        setVerificationRequired(false);
        setSessionMeta(prev => ({ ...(prev || {}), email_verified: true }));
        await fetchSessionMeta(sessionToken, { skipVerificationCheck: true });
        return;
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        "We could not verify the code. Please try again.";
      setEmailError(msg);
    } finally {
      if (!ok) setOtpStep("sent");
    }
  };

  const sendResumeLink = async () => {
    if (!resumeEmail) {
      setResumeStatus("Enter your verified email.");
      return;
    }
    try {
      setResumeStatus("Sending link…");
      const resp = await api.post("/api/open/arrival/session/email/resume", {
        email: resumeEmail.trim(),
        flow_version: "visitor_v1"
      });
      if (resp.data?.success) {
        setResumeStatus("Link sent. Check your inbox.");
      } else {
        setResumeStatus("Please verify this email using the code box below.");
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        "Could not send link. If this email is not verified, please verify below.";
      setResumeStatus(msg);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const cleanFlight = flightNumKey(flightNumber);
    if (!cleanFlight || !flightDate || !flightAnchorUtc) {
      setError("Choose the exact Heathrow flight date and time before creating your guide.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        flight_number: cleanFlight,
        direction: flightDirection,
        flight_date: flightDate,
        anchor_utc: flightAnchorUtc,
        nationality_group: nationalityGroup,
        visit_purpose: "tourist",
        age_group: ageGroup,
        first_time_uk: firstTimeUk,
        visa_type: "none",
        travelling_with_children: travellingWithChildren,
        travelling_with_elderly: travellingWithElderly,
        passengers: Number(passengers) || 1,
        bags: Number(bags) || 0,
        onward_travel_mode: onwardMode
      };

      const res = await api.post("/api/arrival-assistant", payload);
      setResult(res.data);

      const inputs_json = {
        ...payload,
        scenario: res.data?.scenario,
        terminal: res.data?.flight?.terminal,
        scheduled_local: res.data?.timeline?.scheduled_time_display || res.data?.flight?.scheduled_local
      };

      if (sessionToken) {
        try {
          await api.patch(`/api/open/arrival-assistant/session/${sessionToken}/inputs`, {
            inputs_json
          });
          await fetchSessionMeta(sessionToken);
          try {
            localStorage.setItem(STORAGE_KEY, sessionToken);
          } catch (eLS) {
            console.warn("localStorage save skipped", eLS);
          }
        } catch (errPatch) {
          const code = errPatch?.response?.status || null;
          console.error("Session update error:", errPatch);
          if (code === 404 || code === 410) {
            try {
            const sessionRes = await api.post("/api/open/arrival-assistant/session", {
              flow_version: "visitor_v1",
              inputs_json
            });
              setSessionToken(sessionRes.data.session_token);
              setChecklistData(sessionRes.data);
              try {
                localStorage.setItem(STORAGE_KEY, sessionRes.data.session_token);
              } catch (eLS) {
                console.warn("localStorage save skipped", eLS);
              }
              await fetchSessionMeta(sessionRes.data.session_token);
            } catch (errSessionNew) {
              console.error("Fallback session create error:", errSessionNew);
            }
          }
        }
      } else {
        try {
          const sessionRes = await api.post("/api/open/arrival-assistant/session", {
            flow_version: "visitor_v1",
            inputs_json
          });
          setSessionToken(sessionRes.data.session_token);
          setChecklistData(sessionRes.data);
          try {
            localStorage.setItem(STORAGE_KEY, sessionRes.data.session_token);
          } catch (eLS) {
            console.warn("localStorage save skipped", eLS);
          }
          await fetchSessionMeta(sessionRes.data.session_token);
        } catch (errSession) {
          console.error("Checklist session error:", errSession);
        }
      }
    } catch (err) {
      console.error("❌ Arrival assistant error:", err);
      const msg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        "We could not create your arrival guide. Please check your flight number and try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const createFreshSession = async () => {
      setChecklistLoading(true);
      setError(null);
      setResult(null);
      try {
        const sessionRes = await api.post("/api/open/arrival-assistant/session", {
          flow_version: "visitor_v1"
        });

        setSessionToken(sessionRes.data.session_token);
        setChecklistData(sessionRes.data);
        try {
          localStorage.setItem(STORAGE_KEY, sessionRes.data.session_token);
        } catch (eLS) {
          console.warn("localStorage save skipped", eLS);
        }
        await fetchSessionMeta(sessionRes.data.session_token);
      } catch (err) {
        const msg =
          err.response?.data?.error ||
          "We could not start your arrival checklist. Please refresh and try again.";
        setError(msg);
      } finally {
        setChecklistLoading(false);
        setLoading(false);
      }
    };

    const init = async () => {
      let tokenToUse = getTokenFromUrl();

      if (!tokenToUse) {
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) tokenToUse = stored;
        } catch (eLS) {
          console.warn("localStorage read skipped", eLS);
        }
      }

      if (tokenToUse) {
        setSessionToken(tokenToUse);
        const resp = await fetchSessionMeta(tokenToUse);
        if (resp) return;
        // If token missing in DB, create a fresh session and replace storage.
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (eLS) {
          console.warn("localStorage clear skipped", eLS);
        }
        await createFreshSession();
        return;
      }

      await createFreshSession();
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sessionMeta?.email_verified || !checklistData?.sections) return;
    const keys = Object.keys(checklistData.sections);
    if (!keys.length) return;
    if (!keys.includes(activeSection)) {
      const fallback = checklistData.sections["pre_arrival"] ? "pre_arrival" : keys[0];
      setActiveSection(fallback);
    }
  }, [sessionMeta?.email_verified, checklistData, activeSection]);

  const renderSection = (title, lines, defaultOpen = false) => {
    if (!lines || !lines.length) return null;
    const cleanLines = lines.filter(Boolean);
    if (!cleanLines.length) return null;

    return (
      <details className="card arrival-section collapsible" key={title} open={defaultOpen}>
        <summary
          className={`section-summary ${hoveredSection === title ? "hovered" : ""}`}
          onMouseEnter={() => setHoveredSection(title)}
          onMouseLeave={() => setHoveredSection(null)}
        >
          <h3 style={{ margin: 0 }}>{title}</h3>
        </summary>
        <ul className="collapsible-body">
          {cleanLines.map((line, idx) => (
            <li key={idx}>{line}</li>
          ))}
        </ul>
      </details>
    );
  };

  const renderSections = () => {
    if (!checklistData?.sections) return null;

    if (sessionMeta?.email_verified) {
      const sectionKeys = Object.keys(checklistData.sections || {});
      const current = sectionKeys.includes(activeSection) ? activeSection : "pre_arrival";
      const handleChange = val => {
        setActiveSection(val);
        setTimeout(() => {
          const el = document.getElementById(`section-${val}`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
      };
      return (
        <>
          {renderSaveResumeCard()}
          <div className="card arrival-section" style={{ marginBottom: "1rem", padding: "10px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <strong>Open a section</strong>
              <select
                value={current}
                onChange={e => handleChange(e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13 }}
              >
                {sectionKeys.map(key => (
                  <option key={key} value={key}>
                    {checklistData.sections[key]?.title || key}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {renderChecklistSection(current, true)}
        </>
      );
    }

    return (
      <>
        {renderSaveResumeCard()}
        {renderChecklistSection("pre_arrival", true)}
        {renderChecklistSection("itinerary", false)}
        {renderChecklistSection("on_plane", false)}
        {renderChecklistSection("walking_to_border", false)}
        {renderChecklistSection("choosing_queue", false)}
        {renderChecklistSection("immigration_questions", false)}
        {renderChecklistSection("documents_hand_luggage", false)}
        {renderChecklistSection("baggage_reclaim", false)}
        {renderChecklistSection("customs", false)}
        {renderChecklistSection("meeting_driver_or_family", false)}
        {renderChecklistSection("onward_travel", false)}
        {renderChecklistSection("day_one_uk", false)}
        {renderChecklistSection("first_week_uk", false)}
        {renderChecklistSection("life_uk_accommodation", false)}
        {renderChecklistSection("life_uk_work_income", false)}
        {renderChecklistSection("life_uk_travel", false)}
        {renderChecklistSection("life_uk_driving", false)}
        {renderChecklistSection("life_uk_health_safety", false)}
        {renderEmailCard()}
      </>
    );
  };

  const renderSaveResumeCard = () => {
    if (!sessionToken) return null;

    return (
      <div className="card arrival-section" style={{ marginBottom: "1rem", padding: "10px 14px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, fontSize: 13 }}>
          <span style={{ fontWeight: 700 }}>Save your progress (optional)</span>
          <span className="muted" style={{ fontSize: 12 }}>
            Resume later on any device.
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, marginTop: 6, alignItems: "center" }}>
          <input
            type="text"
            readOnly
            value={savedLink}
            className="email-input"
            style={{ minWidth: 180, maxWidth: "100%", margin: 0, fontSize: 12, padding: "8px 10px" }}
          />
          <button type="button" className="btn btn-secondary" style={{ padding: "8px 10px", fontSize: 12 }} onClick={handleCopyLink}>
            {copyStatus === "copied" ? "Copied" : copyStatus === "error" ? "Copy failed" : "Copy link"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ padding: "8px 10px", fontSize: 12 }}
            onClick={() => setShowEmail(prev => !prev)}
          >
            Email link
          </button>
        </div>
        {showEmail && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6, alignItems: "center" }}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="email-input"
              style={{ minWidth: 180, maxWidth: 220, margin: 0, fontSize: 12, padding: "8px 10px" }}
            />
            <button
              type="button"
              className="btn btn-primary"
              style={{ padding: "8px 10px", fontSize: 12 }}
              onClick={sendChecklistEmail}
              disabled={emailSending || !email}
            >
              {emailSending ? "Sending…" : "Send"}
            </button>
          </div>
        )}
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 4, fontSize: 12 }}>
          {emailSent && <span className="success">✅ Sent</span>}
          {emailError && <span className="error">{emailError}</span>}
          {copyStatus === "copied" && <span className="success">Link copied</span>}
          {copyStatus === "error" && <span className="error">Copy failed</span>}
        </div>
      </div>
    );
  };

  const renderChecklistSection = (sectionKey, defaultOpen = false) => {
    const section = checklistData?.sections?.[sectionKey];
    if (!section) return null;
    const savedInputs = checklistData?.inputs_json || {};

    const isVerified = sessionMeta?.email_verified;
    const statusClass =
      section.status === "green"
        ? "status-green"
        : section.status === "amber"
        ? "status-amber"
        : section.status === "blue"
        ? "status-blue"
        : "status-red";

    const doneText =
      typeof section.checkedCount === "number" && typeof section.totalCount === "number"
        ? `${section.checkedCount}/${section.totalCount} done`
        : null;

    if (isVerified) {
      if (sectionKey !== activeSection) return null;
      return (
        <div id={`section-${sectionKey}`} className="card arrival-section">
          <div className={`section-summary section-header header-${section.status || "blue"}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <h3 style={{ margin: 0 }}>{section.title}</h3>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {doneText && <span className="muted" style={{ fontSize: 12 }}>{doneText}</span>}
                <span className={`section-status ${statusClass}`}>
                  {(section.status || "not started").toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          <div className="collapsible-body">
            {section.intro && <p className="muted" style={{ marginTop: 0 }}>{section.intro}</p>}
            {sectionKey === "choosing_queue" && savedInputs?.nationality_group === "NonEEA" && (
              <p className="muted" style={{ marginTop: 0 }}>
                As a Non-EEA passport holder, you’ll usually use the “All other passports” lanes.
              </p>
            )}
            {sectionKey === "choosing_queue" &&
              (savedInputs?.travelling_with_children || savedInputs?.travelling_with_elderly) && (
                <p className="muted" style={{ marginTop: 0 }}>
                  Families and those needing assistance can usually stay together — ask staff if unsure.
                </p>
              )}
            {sectionKey === "baggage_reclaim" && (
              <div className="info-box" style={{ marginTop: 8 }}>
                <strong>Good to know</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  Baggage delivery times vary. Waiting 15–30 minutes after landing is normal, especially for wide-body aircraft.
                </p>
              </div>
            )}
            {sectionKey === "customs" && (
              <div className="info-box" style={{ marginTop: 8 }}>
                <strong>Important</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  UK customs rules change and depend on where you are arriving from. If you are unsure about any item (especially food, medicine, or gifts), use the red channel or ask a customs officer.
                </p>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  Fresh meat, dairy products, and some plants are commonly restricted. When in doubt, declare.
                </p>
                <a href="https://www.gov.uk/bringing-goods-into-uk-personal-use" target="_blank" rel="noreferrer" className="checklist-link">
                  Check official guidance on GOV.UK
                </a>
              </div>
            )}
            {sectionKey === "meeting_driver_or_family" && (
              <div className="info-box" style={{ marginTop: 8 }}>
                <strong>Good to know</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  Heathrow offers free Wi-Fi in all terminals. If you don’t yet have a UK SIM, you can still receive messages or calls via Wi-Fi.
                </p>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  If you have pre-booked a taxi, your driver may hold a name sign or contact you by message. CheapCabGrab drivers can use WhatsApp if you don’t yet have a UK SIM.
                </p>
              </div>
            )}
            {sectionKey === "day_one_uk" && (
              <div className="info-box" style={{ marginTop: 8 }}>
                <strong>Good to know</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  Many visitors feel tired on the first day. Keep plans simple: rest, hydrate, and settle in.
                </p>
                <div style={{ marginTop: 8 }}>
                  <strong>Connectivity</strong>
                  <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                    You can buy a UK SIM or eSIM online or in local shops. Free Wi‑Fi is common in hotels and public places.
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <strong>Accommodation note</strong>
                  <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
                    Keep your hotel or host address handy (with postcode). If you’re staying with friends/family, note their phone number too.
                  </p>
                </div>
              </div>
            )}
            {sectionKey === "first_week_uk" && (
              <div className="info-box" style={{ marginTop: 8 }}>
                <strong>Good to know</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  Keep plans light: explore, stay safe, and keep an eye on travel updates.
                </p>
                <div style={{ marginTop: 8 }}>
                  <strong>Healthcare</strong>
                  <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                    Visitors can access NHS urgent care for emergencies; use your travel insurance for other care.
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <strong>Transport</strong>
                  <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                    Contactless cards work on Tube and buses; check weekend engineering works before long trips.
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <strong>Driving</strong>
                  <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
                    Visitors can sometimes drive with an international licence; check rules and insurance before renting a car.
                  </p>
                </div>
              </div>
            )}
            {sectionKey === "immigration_questions" && savedInputs?.first_time_uk && (
              <p className="muted" style={{ marginTop: 0 }}>
                Many visitors are first-time travellers. Officers expect short, simple answers.
              </p>
            )}
            {sectionKey === "onward_travel" && (
              <div className="info-box" style={{ marginTop: 8 }}>
                <strong>Plan your onward journey</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  Enter your destination postcode to compare options. Choose based on cost, bags, arrival time, and how tired you feel after the flight.
                </p>
                <ul className="mini-list">
                  <li>
                    <a className="checklist-link" href="/heathrow-taxi">
                      Fixed-price taxi (door-to-door)
                    </a>
                  </li>
                  <li>
                    <a className="checklist-link" href="/train-vs-taxi">
                      Train vs taxi (time, changes, walking)
                    </a>
                  </li>
                  <li>
                    <a className="checklist-link" href="/heathrow-parking-vs-taxi">
                      Parking vs taxi (if being collected)
                    </a>
                  </li>
                </ul>
                <p className="muted" style={{ marginTop: 6, marginBottom: 0 }}>
                  If you arrive late or feel exhausted with heavy bags, a door-to-door option can be easier than changing trains.
                </p>
              </div>
            )}

            {checklistLoading && <p className="muted">Loading checklist…</p>}
            {checklistError && <p className="error">{checklistError}</p>}

            {sectionKey === "choosing_queue" && result?.queue_estimate?.wait_message && (
              <p className="muted" style={{ marginTop: 0 }}>
                Immigration Queue expected: {result.queue_estimate.wait_message}
                {result.queue_estimate.terminal && ` (Terminal ${result.queue_estimate.terminal})`}
                . Queues can vary on the day depending on many factors.
              </p>
            )}
            {sectionKey === "immigration_questions" && (
              <div className="info-box">
                <strong>Documents ready if asked:</strong>
                <ul className="mini-list">
                  <li>Passport</li>
                  <li>Visa / ETA confirmation (online or screenshot, if applicable)</li>
                  <li>Accommodation details (address with postcode)</li>
                  <li>Return/onward travel booking</li>
                  <li>Evidence of funds (bank statement / proof of payment if requested)</li>
                  <li>Travel insurance details (if you have cover)</li>
                </ul>
                <p className="muted" style={{ marginTop: 6 }}>
                  You are usually not asked to show all documents, but be ready if requested.
                </p>
                <div style={{ marginTop: 10 }}>
                  <strong>Possible follow-up questions (not everyone is asked):</strong>
                  <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                    In some cases, the officer may ask a few additional questions to understand your visit plans. This is normal, especially for first-time visitors.
                  </p>
                  <ul className="mini-list">
                    <li>Visit purpose: tourism, family, or business meetings.</li>
                    <li>Plans: how long you’re staying, where you’ll stay, and return date.</li>
                    <li>Finances: how you will pay for your trip and daily expenses.</li>
                    <li>Future intentions: plans after your visit, onward travel.</li>
                    <li>Connections in the UK: if you know anyone here and how they’re related.</li>
                  </ul>
                  <p className="muted" style={{ marginTop: 4 }}>
                    Important: there is no “perfect answer”. Answer honestly and consistently with your visa/ETA and travel plans.
                  </p>
                </div>
              </div>
            )}

            <ul className="checklist">
              {section.items.map(item => (
                <li key={item.key} className="checklist-row">
                  <div className="checklist-text">
                    <div className="checklist-label">
                      {item.label}
                      {item.priority === "high" && (
                        <span className="checklist-important">important</span>
                      )}
                      {item.key === "evisa_checked_govuk" && (
                        <a
                          href="https://view-immigration-status.service.gov.uk/status"
                          target="_blank"
                          rel="noreferrer"
                          className="checklist-link"
                        >
                          Check on GOV.UK
                        </a>
                      )}
                {item.key === "arrival_weather_checked" && result?.guide?.weather?.tempC != null && (
                  <span className="mini-badge">
                    Arrival ~{Math.round(result.guide.weather.tempC)}°C
                  </span>
                )}
                      {item.badge && <span className="mini-badge">{item.badge}</span>}
                    </div>
                    {item.help && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        {item.help}
                      </div>
                    )}
                  </div>
                  <div className="checklist-box">
                    <div className="checkbox-pill">
                      <input
                        type="checkbox"
                        checked={!!item.checked}
                        onChange={e => updateChecklistItem(sectionKey, item.key, e.target.checked)}
                        aria-label={item.label}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    return (
      <details
        id={`section-${sectionKey}`}
        className="card arrival-section collapsible"
        defaultOpen={sectionKey === "pre_arrival" ? true : defaultOpen}
      >
        <summary className={`section-summary section-header header-${section.status || "blue"}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <h3 style={{ margin: 0 }}>{section.title}</h3>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {doneText && <span className="muted" style={{ fontSize: 12 }}>{doneText}</span>}
              <span className={`section-status ${statusClass}`}>
                {(section.status || "not started").toUpperCase()}
              </span>
            </div>
          </div>
        </summary>

        <div className="collapsible-body">
          {section.intro && (
            <p className="muted" style={{ marginTop: 0 }}>
              {section.intro}
            </p>
          )}
          {sectionKey === "choosing_queue" && savedInputs?.nationality_group === "NonEEA" && (
            <p className="muted" style={{ marginTop: 0 }}>
              As a Non-EEA passport holder, you’ll usually use the “All other passports” lanes.
            </p>
          )}
          {sectionKey === "choosing_queue" && (savedInputs?.travelling_with_children || savedInputs?.travelling_with_elderly) && (
            <p className="muted" style={{ marginTop: 0 }}>
              Families and those needing assistance can usually stay together — ask staff if unsure.
            </p>
          )}
          {sectionKey === "baggage_reclaim" && (
            <div className="info-box" style={{ marginTop: 8 }}>
              <strong>Good to know</strong>
              <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                Baggage delivery times vary. Waiting 15–30 minutes after landing is normal, especially for wide-body aircraft.
              </p>
            </div>
          )}
          {sectionKey === "customs" && (
            <div className="info-box" style={{ marginTop: 8 }}>
              <strong>Important</strong>
              <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                UK customs rules change and depend on where you are arriving from. If you are unsure about any item (especially food, medicine, or gifts), use the red channel or ask a customs officer.
              </p>
              <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                Fresh meat, dairy products, and some plants are commonly restricted. When in doubt, declare.
              </p>
              <a
                href="https://www.gov.uk/bringing-goods-into-uk-personal-use"
                target="_blank"
                rel="noreferrer"
                className="checklist-link"
              >
                Check official guidance on GOV.UK
              </a>
            </div>
          )}
          {sectionKey === "meeting_driver_or_family" && (
            <div className="info-box" style={{ marginTop: 8 }}>
              <strong>Good to know</strong>
              <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                Heathrow offers free Wi-Fi in all terminals. If you don’t yet have a UK SIM, you can still receive messages or calls via Wi-Fi.
              </p>
              <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                If you have pre-booked a taxi, your driver may hold a name sign or contact you by message. CheapCabGrab drivers can use WhatsApp if you don’t yet have a UK SIM.
              </p>
            </div>
          )}
          {sectionKey === "day_one_uk" && (
            <div className="info-box" style={{ marginTop: 8 }}>
              <strong>Good to know</strong>
              <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                Many visitors feel tired on the first day. Keep plans simple: rest, hydrate, and settle in.
              </p>
              <div style={{ marginTop: 8 }}>
                <strong>Connectivity</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  You can buy a UK SIM or eSIM online or in local shops. Free Wi‑Fi is common in hotels and public places.
                </p>
              </div>
              <div style={{ marginTop: 8 }}>
                <strong>Accommodation note</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
                  Keep your hotel or host address handy (with postcode). If you’re staying with friends/family, note their phone number too.
                </p>
              </div>
            </div>
          )}
          {sectionKey === "first_week_uk" && (
            <div className="info-box" style={{ marginTop: 8 }}>
              <strong>Good to know</strong>
              <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                Keep plans light: explore, stay safe, and keep an eye on travel updates.
              </p>
              <div style={{ marginTop: 8 }}>
                <strong>Healthcare</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  Visitors can access NHS urgent care for emergencies; use your travel insurance for other care.
                </p>
              </div>
              <div style={{ marginTop: 8 }}>
                <strong>Transport</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  Contactless cards work on Tube and buses; check weekend engineering works before long trips.
                </p>
              </div>
              <div style={{ marginTop: 8 }}>
                <strong>Driving</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
                  Visitors can sometimes drive with an international licence; check rules and insurance before renting a car.
                </p>
              </div>
            </div>
          )}
          {sectionKey === "immigration_questions" && savedInputs?.first_time_uk && (
            <p className="muted" style={{ marginTop: 0 }}>
              Many visitors are first-time travellers. Officers expect short, simple answers.
            </p>
          )}
          {sectionKey === "onward_travel" && (
            <div className="info-box" style={{ marginTop: 8 }}>
              <strong>Plan your onward journey</strong>
              <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                Enter your destination postcode to compare options. Choose based on cost, bags, arrival time, and how tired you feel after the flight.
              </p>
              <ul className="mini-list">
                <li>
                  <a className="checklist-link" href="/heathrow-taxi">
                    Fixed-price taxi (door-to-door)
                  </a>
                </li>
                <li>
                  <a className="checklist-link" href="/train-vs-taxi">
                    Train vs taxi (time, changes, walking)
                  </a>
                </li>
                <li>
                  <a className="checklist-link" href="/heathrow-parking-vs-taxi">
                    Parking vs taxi (if being collected)
                  </a>
                </li>
              </ul>
              <p className="muted" style={{ marginTop: 6, marginBottom: 0 }}>
                If you arrive late or feel exhausted with heavy bags, a door-to-door option can be easier than changing trains.
              </p>
            </div>
          )}

          {checklistLoading && <p className="muted">Loading checklist…</p>}
          {checklistError && <p className="error">{checklistError}</p>}

          {sectionKey === "choosing_queue" && result?.queue_estimate?.wait_message && (
            <p className="muted" style={{ marginTop: 0 }}>
              Immigration Queue expected: {result.queue_estimate.wait_message}
              {result.queue_estimate.terminal && ` (Terminal ${result.queue_estimate.terminal})`}
              . Queues can vary on the day depending on many factors.
            </p>
          )}
          {sectionKey === "immigration_questions" && (
            <div className="info-box">
              <strong>Documents ready if asked:</strong>
              <ul className="mini-list">
                <li>Passport</li>
                <li>Visa / ETA confirmation (online or screenshot, if applicable)</li>
                <li>Accommodation details (address with postcode)</li>
                <li>Return/onward travel booking</li>
                <li>Evidence of funds (bank statement / proof of payment if requested)</li>
                <li>Travel insurance details (if you have cover)</li>
              </ul>
              <p className="muted" style={{ marginTop: 6 }}>
                You are usually not asked to show all documents, but be ready if requested.
              </p>
              <div style={{ marginTop: 10 }}>
                <strong>Possible follow-up questions (not everyone is asked):</strong>
                <p className="muted" style={{ marginTop: 4, marginBottom: 6 }}>
                  In some cases, the officer may ask a few additional questions to understand your visit plans. This is normal, especially for first-time visitors.
                </p>
                <ul className="mini-list">
                  <li>Visit purpose: tourism, family, or business meetings.</li>
                  <li>Plans: how long you’re staying, where you’ll stay, and return date.</li>
                  <li>Finances: how you will pay for your trip and daily expenses.</li>
                  <li>Future intentions: plans after your visit, onward travel.</li>
                  <li>Connections in the UK: if you know anyone here and how they’re related.</li>
                </ul>
                <p className="muted" style={{ marginTop: 4 }}>
                  Important: there is no “perfect answer”. Answer honestly and consistently with your visa/ETA and travel plans.
                </p>
              </div>
            </div>
          )}

          <ul className="checklist">
            {section.items.map(item => (
              <li key={item.key} className="checklist-row">
                <div className="checklist-text">
                  <div className="checklist-label">
                    {item.label}
                    {item.priority === "high" && (
                      <span className="checklist-important">important</span>
                    )}
                    {item.key === "evisa_checked_govuk" && (
                      <a
                        href="https://view-immigration-status.service.gov.uk/status"
                        target="_blank"
                        rel="noreferrer"
                        className="checklist-link"
                      >
                        Check on GOV.UK
                      </a>
                    )}
                {item.key === "arrival_weather_checked" && result?.guide?.weather?.tempC != null && (
                  <span className="mini-badge">
                    Arrival ~{Math.round(result.guide.weather.tempC)}°C
                  </span>
                )}
                    {item.badge && <span className="mini-badge">{item.badge}</span>}
                  </div>
                  {item.help && (
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      {item.help}
                    </div>
                  )}
                </div>
                <div className="checklist-box">
                  <div className="checkbox-pill">
                    <input
                      type="checkbox"
                      checked={!!item.checked}
                      onChange={e => updateChecklistItem(sectionKey, item.key, e.target.checked)}
                      aria-label={item.label}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {sessionToken && (
            <p className="muted" style={{ marginTop: 12 }}>
              Saved link: <code>?s={sessionToken}</code>
            </p>
          )}

          {section.footer && (
            <p className="muted" style={{ marginTop: 8 }}>
              {section.footer}
            </p>
          )}
        </div>
      </details>
    );
  };

  const overallProgress = () => {
    if (!checklistData?.sections) return { pct: 0, done: 0, total: 0 };
    let done = 0;
    let total = 0;
    Object.values(checklistData.sections).forEach(sec => {
      const d = sec.checkedCount || 0;
      const t = sec.totalCount || 0;
      done += d;
      total += t;
    });
    const pct = total ? Math.round((done / total) * 100) : 0;
    return { pct, done, total };
  };

  const renderDashboard = () => {
    if (!checklistData?.sections) return null;
    const { pct, done, total } = overallProgress();
    return (
      <div className="card arrival-section" style={{ marginBottom: "1rem" }}>
        <h3 style={{ marginTop: 0 }}>Welcome back{emailHint ? `, ${emailHint}` : sessionMeta?.email ? `, ${sessionMeta.email}` : ""}</h3>
        <p className="muted" style={{ marginTop: 2 }}>Your verified checklist. Progress saves across devices.</p>
        <div style={{ background: "#eef2ff", borderRadius: 10, padding: 10, marginTop: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span>Overall progress</span>
            <span>{done}/{total} ({pct}%)</span>
          </div>
          <div style={{ height: 10, background: "#d9e1ff", borderRadius: 999, marginTop: 6 }}>
            <div style={{ width: `${pct}%`, height: "100%", background: "#3358ff", borderRadius: 999 }} />
          </div>
        </div>
        <div className="home-grid-3" style={{ marginTop: 12 }}>
          {Object.entries(checklistData.sections).map(([key, sec]) => (
            <article
              key={key}
              className="home-card"
              style={{ padding: 12, cursor: "pointer" }}
              onClick={() => {
                setActiveSection(key);
                setTimeout(() => {
                  const el = document.getElementById(`section-${key}`);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 50);
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <strong style={{ fontSize: 14 }}>{sec.title}</strong>
                <span className={`section-status status-${sec.status || "blue"}`} style={{ fontSize: 11 }}>
                  {(sec.status || "BLUE").toUpperCase()}
                </span>
              </div>
              <div className="muted" style={{ fontSize: 12 }}>{sec.checkedCount || 0}/{sec.totalCount || 0} done</div>
            </article>
          ))}
        </div>
      </div>
    );
  };

  const renderOtpGate = () => {
    return (
      <div className="card arrival-section" style={{ marginBottom: "1rem", padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Verify your email to continue</h3>
        <p className="muted" style={{ marginTop: 4 }}>
          We need to verify this session on this device. Enter your email to get a code.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder={emailHint || "you@example.com"}
            className="email-input"
            style={{ minWidth: 220, fontSize: 12, padding: "8px 10px" }}
          />
          <button
            type="button"
            className="btn btn-primary"
            style={{ padding: "8px 12px", fontSize: 12 }}
            onClick={sendOtp}
            disabled={!sessionToken}
          >
            {otpStep === "sent" ? "Code sent" : "Send code"}
          </button>
        </div>
        {otpStep === "sent" && (
          <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="text"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value)}
              placeholder="Enter 6-digit code"
              className="email-input"
              style={{ minWidth: 160, fontSize: 12, padding: "8px 10px" }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              style={{ padding: "8px 12px", fontSize: 12 }}
              onClick={verifyOtp}
              disabled={otpStep === "verifying"}
            >
              {otpStep === "verifying" ? "Verifying…" : "Verify"}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              Sent to {emailHint || email || "your email"} (expires in 15 min)
            </span>
          </div>
        )}
        {emailError && <p className="error" style={{ marginTop: 8 }}>{emailError}</p>}
        {emailSent && otpStep === "sent" && <p className="success" style={{ marginTop: 8 }}>Code sent. Check your inbox.</p>}
      </div>
    );
  };

  const renderVerifyInline = () => {
    if (verificationRequired || sessionMeta?.email_verified) return null;
    if (!sessionToken) return null;
    return (
      <div className="card arrival-section" style={{ marginBottom: "1rem", padding: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>Verify this email (optional)</span>
          <span className="muted" style={{ fontSize: 12 }}>Unlock cross-device progress.</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginTop: 8, alignItems: "center" }}>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder={emailHint || "you@example.com"}
            className="email-input"
            style={{ minWidth: 200, fontSize: 12, padding: "8px 10px", margin: 0 }}
          />
          <button
            type="button"
            className="btn btn-secondary"
            style={{ padding: "8px 12px", fontSize: 12 }}
            onClick={sendOtp}
          >
            {otpStep === "sent" ? "Code sent" : "Send code"}
          </button>
        </div>
        {otpStep === "sent" && (
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="text"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value)}
              placeholder="Enter 6-digit code"
              className="email-input"
              style={{ minWidth: 160, fontSize: 12, padding: "8px 10px", margin: 0 }}
            />
            <button
              type="button"
              className="btn btn-primary"
              style={{ padding: "8px 12px", fontSize: 12 }}
              onClick={verifyOtp}
              disabled={otpStep === "verifying"}
            >
              {otpStep === "verifying" ? "Verifying…" : "Verify"}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              Sent to {emailHint || email || "your email"}
            </span>
          </div>
        )}
        {emailError && <p className="error" style={{ marginTop: 6 }}>{emailError}</p>}
        {emailSent && otpStep === "sent" && <p className="success" style={{ marginTop: 6 }}>Code sent. Check your inbox.</p>}
      </div>
    );
  };

  const renderResumeStrip = () => {
    return (
      <div className="card arrival-section" style={{ marginBottom: "1rem", padding: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 13 }}>
          <strong>Already verified?</strong>
          <span className="muted">Get your resume link by email.</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginTop: 6, alignItems: "center" }}>
          <input
            type="email"
            value={resumeEmail}
            onChange={e => setResumeEmail(e.target.value)}
            placeholder="you@example.com"
            className="email-input"
            style={{ minWidth: 200, fontSize: 12, padding: "8px 10px", margin: 0 }}
          />
          <button
            type="button"
            className="btn btn-secondary"
            style={{ padding: "8px 12px", fontSize: 12 }}
            onClick={sendResumeLink}
          >
            Send my link
          </button>
        </div>
        {resumeStatus && <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>{resumeStatus}</p>}
        <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          If this email is not verified yet, use the code box below to verify once.
        </p>
      </div>
    );
  };

  const renderMainContent = () => {
    if (verificationRequired) {
      return renderOtpGate();
    }
    if (sessionMeta?.email_verified) {
      return (
        <>
          {renderDashboard()}
          {renderSections()}
        </>
      );
    }
    return (
      <>
        {renderResumeStrip()}
        {renderVerifyInline()}
        {renderSections()}
      </>
    );
  };

  const renderEmailCard = () => {
    if (!sessionToken) return null;
    return (
      <section className="card arrival-section email-card">
        <h3 style={{ marginTop: 0 }}>Email my checklist</h3>
        <p className="muted" style={{ marginTop: 6 }}>
          We’ll send you a summary and a link to reopen your saved checklist.
        </p>
        <div className="email-row">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="email-input"
          />
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={sendChecklistEmail}
          disabled={emailSending || !email?.includes("@")}
        >
          {emailSending ? "Sending…" : "Send"}
        </button>
        {emailSent && <p className="success">✅ Email sent. Check your inbox (and spam folder).</p>}
        {emailError && <p className="error">{emailError}</p>}
        <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
          We only use your email to send this checklist.
        </p>
      </section>
    );
  };

  const renderSummaryHeader = () => {
    if (!result) return null;

    const { scenario, flight, timeline, queue_estimate } = result;
    const weather = result?.guide?.weather;

    return (
      <section className="card arrival-summary">
        <h2>Your personalised Heathrow guide</h2>

        <p className="lede">{result.guide?.summary}</p>

        {result?.guide?.risk_profile && (
          <p className="muted">
            Support level:{" "}
            <strong>
              {result.guide.risk_profile.band.replace("_", " ")}
            </strong>{" "}
            (score {result.guide.risk_profile.score})
          </p>
        )}

        {result?.guide?.risk_profile && (
          <p className="muted">
            Examples: a first-time family visitor will see more support, while a frequent traveller sees a shorter script.
          </p>
        )}

        <div className="summary-grid">
          <div>
            <h4>Flight details</h4>
            <p>
              <strong>
                {flight?.iata_flight_identifier || flight?.flight_number}
              </strong>{" "}
              {flight?.airline_name && <>· {flight.airline_name}</>}
            </p>
            <p>
              {scenario === "ARRIVAL" ? "Arriving into" : "Departing from"} Terminal{" "}
              {flight?.terminal || "TBC"}
            </p>
            <p>
              Scheduled local time:{" "}
              {timeline?.scheduled_time_display || flight?.scheduled_local}
            </p>
            {flight?.origin_airport_name && scenario === "ARRIVAL" && (
              <p>
                From: {flight.origin_airport_name}
                {flight.origin_city ? ` (${flight.origin_city})` : ""}
              </p>
            )}
            {flight?.destination_airport_name && scenario === "DEPARTURE" && (
              <p>
                To: {flight.destination_airport_name}
                {flight.destination_city ? ` (${flight.destination_city})` : ""}
              </p>
            )}
          </div>

          <div>
            <h4>Time inside the terminal</h4>
            {scenario === "ARRIVAL" && timeline && (
              <p>
                From landing to leaving the arrivals hall, allow{" "}
                <strong>
                  {timeline.suggested_min_total_minutes}–
                  {timeline.suggested_max_total_minutes} minutes
                </strong>{" "}
                for immigration, baggage and walking.
              </p>
            )}
            {!timeline && <p>We could not build a time estimate for this flight.</p>}

            {queue_estimate && (
              <>
                <h4 style={{ marginTop: "0.75rem" }}>
                  {scenario === "ARRIVAL" ? "Immigration queues" : "Security queues"}
                </h4>
                <p>
                  Latest snapshot for Terminal {queue_estimate.terminal}:{" "}
                  {queue_estimate.wait_message || "typical queues"}.
                </p>
                {queue_estimate.derived_range && (
                  <p>Range: {queue_estimate.derived_range}</p>
                )}
                <p className="muted">
                  Snapshot time: {queue_estimate.snapshot_time_display}
                </p>
              </>
            )}
          </div>

          {weather && (
            <div className="weather-card">
              <h4>Weather around arrival</h4>
              <p>
                Expected: <strong>{Math.round(weather.tempC)}°C</strong>
                {weather.precipProb != null && (
                  <> · Rain chance: {Math.round(weather.precipProb)}%</>
                )}
                {weather.windKph != null && (
                  <> · Wind: {Math.round(weather.windKph)} km/h</>
                )}
              </p>
              {weather.summary && <p className="muted">{weather.summary}</p>}
              {weather.items?.length > 0 && (
                <ul className="weather-items">
                  {weather.items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              )}
              {weather.notes?.length > 0 && (
                <ul className="weather-notes muted">
                  {weather.notes.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <p className="disclaimer">{result.disclaimer}</p>
      </section>
    );
  };

  return (
    <>
      <Helmet>
        <title>
          Heathrow Arrival Assistant for Visitors | Queue expectations, customs, onward travel
        </title>
        <meta
          name="description"
          content="Visitor-focused Heathrow arrival guide: immigration questions, queue expectations, baggage/customs tips, onward travel, and stay-safe checklists."
        />
        <meta
          name="keywords"
          content="Heathrow arrival visitor, UK visitor checklist, Heathrow immigration questions for tourists, customs UK, Heathrow taxi, Heathrow train, travel from Heathrow"
        />
        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/arrival-assistant-visitor"
        />
        <meta
          property="og:title"
          content="Heathrow Arrival Assistant for Visitors | Immigration & Travel Tips"
        />
        <meta
          property="og:description"
          content="Personalised Heathrow arrival guide for visitors with immigration questions, queue snapshots, baggage/customs reminders, and onward travel tips."
        />
        <meta
          property="og:url"
          content="https://www.cheapcabgrab.com/arrival-assistant-visitor"
        />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="arrival-assistant-page">

        <header className="page-header">
          <h1>Heathrow Arrival Assistant for Visitors</h1>
          <p className="muted" style={{ marginTop: 4 }}>Updated: {todayUpdatedLabel}</p>
          <p>
            A simple, data-based guide to help visitors and holidaymakers arrive smoothly at Heathrow. Immigration, baggage, customs, and onward travel with saved progress.
          </p>
        </header>

        <section className="card arrival-section" style={{ marginBottom: "1rem" }}>
          <h3 style={{ marginTop: 0 }}>How this helps</h3>
          <p className="muted" style={{ marginTop: 6 }}>
            Visitor-focused checklists: immigration Q&A, customs, onward travel, and stay-safe tips for holidays.
            Verify your email once to unlock:
          </p>
          <ul className="mini-list" style={{ marginTop: 6 }}>
            <li>Cross-device progress with a verified dashboard and section picker.</li>
            <li>Email resume links plus trusted-device support to avoid repeat OTPs on the same browser.</li>
            <li>Compact verified view to open one section at a time with progress totals.</li>
          </ul>
        </section>

        <section className="card arrival-form-card">
          <h2>Tell us about your trip</h2>
          <p>We use these details to shape your guide. Short, simple and personalised.</p>

          <form onSubmit={handleSubmit} className="form-grid">
            <HeathrowFlightSelector
              flightNumber={flightNumber}
              setFlightNumber={setFlightNumber}
              flightDirection={flightDirection}
              setFlightDirection={setFlightDirection}
              flightDate={flightDate}
              setFlightDate={setFlightDate}
              flightAnchorUtc={flightAnchorUtc}
              setFlightAnchorUtc={setFlightAnchorUtc}
              note="Choose the exact Heathrow arrival or departure you want guidance for."
            />

            <div className="form-group">
              <label>Passport / nationality group</label>
              <select
                value={nationalityGroup}
                onChange={e => setNationalityGroup(e.target.value)}
              >
                <option value="UK">UK passport / returning resident</option>
                <option value="EEA">EU / EEA / Swiss passport</option>
                <option value="NonEEA">Non-EEA passport</option>
              </select>
            </div>

            <div className="form-group">
              <label>Reason for visit</label>
              <select
                value={visitPurpose}
                disabled
              >
                <option value="tourist">Tourist / Holiday</option>
              </select>
            </div>

            <div className="form-group">
              <label>Age group (main traveller)</label>
              <select value={ageGroup} onChange={e => setAgeGroup(e.target.value)}>
                <option value="child">Child / Teen</option>
                <option value="adult">Adult</option>
                <option value="senior">Senior</option>
              </select>
            </div>

            <div className="form-group">
              <label>Is this your first visit to the UK?</label>
              <div className="inline-options">
                <button
                  type="button"
                  className={firstTimeUk ? "btn btn-primary" : "btn btn-outline"}
                  onClick={() => setFirstTimeUk(true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={!firstTimeUk ? "btn btn-primary" : "btn btn-outline"}
                  onClick={() => setFirstTimeUk(false)}
                >
                  No
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Visa type (if any)</label>
              <select value={visaType} disabled>
                <option value="none">No visa (visitor/ETA/visa-free)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Travelling with children?</label>
              <div className="inline-options">
                <button
                  type="button"
                  className={
                    travellingWithChildren ? "btn btn-primary" : "btn btn-outline"
                  }
                  onClick={() => setTravellingWithChildren(true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={
                    !travellingWithChildren ? "btn btn-primary" : "btn btn-outline"
                  }
                  onClick={() => setTravellingWithChildren(false)}
                >
                  No
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Travelling with elderly family?</label>
              <div className="inline-options">
                <button
                  type="button"
                  className={
                    travellingWithElderly ? "btn btn-primary" : "btn btn-outline"
                  }
                  onClick={() => setTravellingWithElderly(true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={
                    !travellingWithElderly ? "btn btn-primary" : "btn btn-outline"
                  }
                  onClick={() => setTravellingWithElderly(false)}
                >
                  No
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>How do you plan to travel from Heathrow?</label>
              <select
                value={onwardMode}
                onChange={e => setOnwardMode(e.target.value)}
              >
                <option value="undecided">I’m not sure yet</option>
                <option value="prebooked_taxi">Pre-booked taxi / minicab</option>
                <option value="uber_app">Uber / app-based taxi on arrival</option>
                <option value="train">Train / Underground / Elizabeth line</option>
                <option value="family_pickup">Family or friends collecting by car</option>
              </select>
            </div>

            <div className="form-group small">
              <label>Number of passengers</label>
              <input
                type="number"
                min="1"
                value={passengers}
                onChange={e => setPassengers(e.target.value)}
              />
            </div>

            <div className="form-group small">
              <label>Checked bags</label>
              <input
                type="number"
                min="0"
                value={bags}
                onChange={e => setBags(e.target.value)}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Creating your arrival guide…" : "Get my arrival guide"}
              </button>
            </div>
          </form>

          {error && <p className="error">{error}</p>}
        </section>

        <h2 style={{ marginTop: "1.5rem" }}>Step-by-step Heathrow arrival guide (visitor)</h2>
        {renderSummaryHeader()}
        <h3 style={{ marginTop: "1.25rem" }}>Arrival checklists</h3>
        {renderMainContent()}

        <section className="card arrival-section">
          <div style={{ marginTop: 0 }}>
            <strong>Plan your journey:</strong>
            <ul style={{ marginTop: 8 }}>
              <li>
                <a href="/heathrow-taxi">Heathrow Taxi Transfers</a>
              </li>
              <li>
                <a href="/train-vs-taxi">Taxi vs Train Comparison</a>
              </li>
              <li>
                <a href="/heathrow-parking-vs-taxi">Parking vs Taxi Comparison</a>
              </li>
              <li>
                <a href="/terminalFinder">Find Heathrow Terminals</a>
              </li>
              <li>
                <a href="/airlineContacts">Airline Contact Numbers</a>
              </li>
              <li>
                <a href="/travelnews">UK Travel News</a>
              </li>
            </ul>
          </div>
        </section>

        <section className="card arrival-section">
          <h3>How we built this guide</h3>
          <p className="muted" style={{ marginTop: 6 }}>
            Built for visitors using Heathrow flight data, queue snapshots, and official UK guidance. We refresh advice regularly so you can prepare with confidence.
          </p>
          <ul className="mini-list">
            <li>Arrival timings based on recent Heathrow queue snapshots and flight schedules.</li>
            <li>Immigration & customs reminders aligned with official UK guidance.</li>
            <li>Practical checklists to reduce stress with luggage, queues, and onward travel.</li>
          </ul>
          <p className="muted" style={{ marginTop: 10 }}>
            Official references:
          </p>
          <ul className="mini-list">
            <li><a className="checklist-link" href="https://www.gov.uk/uk-visa" target="_blank" rel="noreferrer">UK visa and immigration</a></li>
            <li><a className="checklist-link" href="https://www.gov.uk/check-uk-visa" target="_blank" rel="noreferrer">Check if you need a visa</a></li>
            <li><a className="checklist-link" href="https://www.gov.uk/entering-the-uk" target="_blank" rel="noreferrer">Arriving at the UK border</a></li>
            <li><a className="checklist-link" href="https://www.gov.uk/bringing-goods-into-uk-personal-use" target="_blank" rel="noreferrer">Customs: what you can bring</a></li>
          </ul>
        </section>

        <ReviewsBar pageKey="/arrival-assistant-visitor" />
      </div>
    </>
  );
};

export default ArrivalAssistantVisitor;
